<template>
  <v-card
    width="100%"
    class="search-history"
    :class="{ 'search-history--dark': dark }"
    flat
  >
    <header class="search-history__header">
      <v-list class="py-0">
        <v-list-item class="search-history__title px-2 ma-0">
          <v-list-item-title class="font-weight-bold subtitle-1 search-history__title-text">
            <span>Recent Searches</span>
            <sup class="search-history__help-sup">
              <a
                href="https://docs.tendertrace.com/guide/user-documentation/saving-your-work/search-history"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Recent searches documentation"
              >
                <v-icon x-small :color="dark ? 'white' : 'secondary'">mdi-help-circle-outline</v-icon>
              </a>
            </sup>
          </v-list-item-title>
          <v-btn
            icon
            aria-label="Close recent searches"
            @click.stop="$emit('closeDrawer')"
          >
            <v-icon class="search-history__close-icon">mdi-close</v-icon>
          </v-btn>
        </v-list-item>
      </v-list>

      <v-divider class="search-history__divider"></v-divider>
      <v-card flat class="search-history__search-wrap px-2 py-2 mx-2 my-2">
        <v-text-field
          v-model="search"
          dense
          :solo="dark"
          :solo-inverted="!dark"
          :light="dark"
          hide-details
          flat
          rounded
          label="Search recent searches..."
          append-icon="mdi-magnify"
          color="rgb(255, 20, 99)"
          :background-color="dark ? '#ffffff' : undefined"
          class="search-history__search-field"
          :class="{ 'search-history__search-field--dark': dark }"
        ></v-text-field>
      </v-card>
    </header>

    <div class="search-history__body">
      <div v-if="loading" class="search-history__loading text-center py-8">
        <v-progress-circular indeterminate color="#ff1463" />
      </div>
      <v-list v-else class="py-0 search-history__list">
        <v-list-item-group
          v-if="filteredRecentSearches.length > 0"
          v-model="selectedItem"
          color="rgb(255, 20, 99)"
        >
          <v-list-item
            v-for="item in filteredRecentSearches"
            :key="item.id"
            class="search-history__row"
            @click="retrieveSearch(item.search_query)"
          >
            <v-tooltip
              left
              dark
              color="#000000"
              style="opacity: 0.7;"
              :disabled="!isQueryTruncated(item.id)"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-list-item-content v-bind="attrs" v-on="on" class="py-2">
                  <v-list-item-title
                    class="search-history__item font-weight-light subtitle-2"
                  >
                    <span
                      :ref="(el) => setQueryTitleRef(item.id, el)"
                      class="search-history__query-text"
                    >
                      {{ item.plain_text_query }}
                    </span>
                  </v-list-item-title>
                </v-list-item-content>
              </template>
              <span class="search-history__query-tooltip">{{ item.plain_text_query }}</span>
            </v-tooltip>
          </v-list-item>
        </v-list-item-group>
        <v-list-item v-else class="ma-0 pa-0">
          <v-card flat class="ma-0 pa-0">
            <v-card-text class="search-history__empty-text">
              <p>
                Tendertrace automatically saves queries you run so you can quickly rerun a
                search even if you are not tracking it. Your most recent 20 queries appear here.
              </p>
              <p class="d-flex align-center flex-wrap">
                Open this list anytime from the
                <v-icon small class="mx-1 grey--text">mdi-history</v-icon>
                history icon in the search bar.
              </p>
              <p>
                <router-link
                  class="search-history__explainer-link"
                  :to="{
                    name: 'search',
                    query: {
                      disposition: ['Contract Notice', 'Grant'],
                      expiry_from: new Date().getFullYear(),
                      expiry_to: new Date().getFullYear(),
                      sort_order: 'asc',
                    },
                  }"
                >
                  Click here to start exploring.
                </router-link>
              </p>
            </v-card-text>
          </v-card>
        </v-list-item>
      </v-list>
    </div>
  </v-card>
</template>

<script>
import { bus } from '../main.js';
import axios from 'axios';
import { getInstance } from '../auth/index.js';

const RECENT_SEARCH_LIMIT = 20;

export default {
  props: {
    dark: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: true,
      recent_searches: [],
      selectedItem: Number,
      search: '',
      truncatedQueryIds: {},
    };
  },

  mounted() {
    this.getRecentSearches();

    bus.$on('refresh_lists', this.onRefreshLists);
    bus.$on('search-complete', this.onSearchComplete);
  },

  beforeDestroy() {
    bus.$off('refresh_lists', this.onRefreshLists);
    bus.$off('search-complete', this.onSearchComplete);
  },

  computed: {
    filteredRecentSearches() {
      if (this.recent_searches.length === 0) {
        return this.recent_searches;
      }
      const term = this.search.toLowerCase();
      return this.recent_searches.filter((row) =>
        (row.plain_text_query || '').toLowerCase().includes(term)
      );
    },
  },

  watch: {
    filteredRecentSearches() {
      this.scheduleTruncationMeasure();
    },
    loading(isLoading) {
      if (!isLoading) {
        this.scheduleTruncationMeasure();
      }
    },
  },

  methods: {
    setQueryTitleRef(id, el) {
      const key = String(id);
      if (el) {
        if (!this._queryTitleElements) {
          this._queryTitleElements = {};
        }
        this._queryTitleElements[key] = el;
      } else if (this._queryTitleElements) {
        delete this._queryTitleElements[key];
      }
    },

    scheduleTruncationMeasure() {
      this.$nextTick(() => {
        this.$nextTick(this.measureQueryTruncation);
      });
    },

    measureQueryTruncation() {
      const elements = this._queryTitleElements || {};
      const next = {};
      Object.keys(elements).forEach((id) => {
        const el = elements[id];
        if (el) {
          next[id] = el.scrollHeight > el.clientHeight + 1;
        }
      });
      this.truncatedQueryIds = next;
    },

    isQueryTruncated(id) {
      return !!this.truncatedQueryIds[String(id)];
    },

    onRefreshLists() {
      this.getRecentSearches();
    },

    onSearchComplete() {
      this.getRecentSearches();
    },

    normalizeSearchQuery(query) {
      if (!query) {
        return {};
      }
      if (typeof query === 'string') {
        return JSON.parse(query);
      }
      return { ...query };
    },

    retrieveSearch(query) {
      const qry = this.normalizeSearchQuery(query);
      this.$router.push({ path: '/search', query: qry });
      bus.$emit('hide-side-navs', true);
    },

    async getRecentSearches() {
      this.loading = true;
      try {
        const instance = getInstance();
        const accessToken = await instance.getTokenSilently();
        const response = await axios.get(process.env.VUE_APP_API_SEARCHES, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const history = (response.data && response.data.search_history) || [];
        this.recent_searches = history.slice(0, RECENT_SEARCH_LIMIT);
      } catch (error) {
        this.recent_searches = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.search-history {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.search-history__header {
  flex-shrink: 0;
}

.search-history__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.search-history__title.v-list-item {
  min-height: 48px;
}

.search-history__title-text {
  color: #12253a !important;
}

.search-history__help-sup {
  line-height: 0;
  margin-left: 6px;
}

.search-history__help-sup a {
  text-decoration: none;
  opacity: 0.85;
}

.search-history__help-sup a:hover {
  opacity: 1;
}

.search-history__close-icon {
  color: rgba(0, 0, 0, 0.54) !important;
}

.search-history__row {
  cursor: pointer;
}

.search-history__row:not(:last-child) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.search-history__item {
  color: #12253a !important;
  font-weight: 300 !important;
  white-space: normal !important;
}

.search-history__query-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
}

.search-history__query-tooltip {
  display: inline-block;
  max-width: 360px;
}

.search-history__empty-text {
  color: #12253a;
  font-size: 0.875rem;
}

.search-history__explainer-link {
  color: #12253a;
  font-weight: 600;
}

.search-history--dark {
  background: transparent !important;
}

.search-history--dark .search-history__title-text {
  color: rgba(255, 255, 255, 0.95) !important;
}

.search-history--dark .search-history__close-icon {
  color: rgba(255, 255, 255, 0.9) !important;
}

.search-history--dark .search-history__divider {
  border-color: rgba(255, 255, 255, 0.12);
}

.search-history--dark .search-history__row:not(:last-child) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.search-history--dark .search-history__item {
  color: rgba(255, 255, 255, 0.9) !important;
  font-weight: 300 !important;
}

.search-history--dark .search-history__list >>> .v-list-item {
  min-height: 48px;
}

.search-history__list >>> .v-list-item__title {
  white-space: normal !important;
  -webkit-line-clamp: unset !important;
}

.search-history--dark .search-history__empty-text {
  color: rgba(255, 255, 255, 0.85);
}

.search-history--dark .search-history__explainer-link {
  color: #ff1463 !important;
}

.search-history--dark .search-history__search-wrap {
  background: transparent !important;
}

.search-history__search-field--dark >>> .v-input__slot {
  background: #ffffff !important;
  box-shadow: none !important;
}

.search-history__search-field--dark >>> fieldset {
  border: 1px solid #12253a !important;
}

.search-history__search-field--dark >>> .v-input__append-inner .v-icon,
.search-history__search-field--dark >>> .v-input__append-outer .v-icon {
  color: #ff1463 !important;
}

.search-history__search-field--dark >>> input {
  color: #12253a !important;
  caret-color: #12253a;
}

.search-history__search-field--dark >>> .v-label {
  color: rgba(18, 37, 58, 0.6) !important;
}

.search-history__search-field--dark.v-input--is-focused >>> fieldset {
  border-color: #12253a !important;
}

</style>

<!-- Unscoped: override drawer theme--dark on solo fields (same as Favourites in nav drawer) -->
<style>
.search-history.search-history--dark .search-history__search-field--dark.v-text-field.v-input--is-solo > .v-input__control > .v-input__slot {
  background: #ffffff !important;
  box-shadow: none !important;
}

.search-history.search-history--dark .search-history__search-field--dark.v-text-field .v-input__append-inner .v-icon,
.search-history.search-history--dark .search-history__search-field--dark.v-text-field .v-input__append-outer .v-icon {
  color: #ff1463 !important;
}

.search-history.search-history--dark .search-history__search-field--dark.v-text-field input {
  color: #12253a !important;
  caret-color: #12253a;
}

.search-history.search-history--dark .search-history__search-field--dark.v-text-field .v-label {
  color: rgba(18, 37, 58, 0.6) !important;
}
</style>
